import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import image1 from '../../assets/pageimages/TransNations.webp';

const TransNationAbout = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();

  const handleTabClick = (tab) => {
    setActiveTab(tab.id);
  };

  const tabs = [
    { id: "ib", label: "💡 International Business" },
    { id: "overview", label: "🌍 Overview" },
    { id: "modules", label: "🧩 Modules" },
    { id: "features", label: "✨ Features" },
    { id: "future", label: "🚀 TransNation X" },
    { id: "start", label: "🛣️ How to Start" },
  ];

  const modules = [
    { title: "Global Expansion Engine", emoji: "🧭", desc: "Explore countries, plan market entry, and discover partners globally." },
    { title: "Trade Management", emoji: "📦", desc: "Manage orders, logistics, invoicing, and global product catalogs." },
    { title: "Compliance & Trust", emoji: "🧾", desc: "ISO compliance, trust scoring, regulations, and document vault." },
    { title: "Global Workforce", emoji: "👨‍💻", desc: "Hire talent, manage remote teams, and build global workforce." },
    { title: "Funding Access", emoji: "💰", desc: "Connect with investors, loans, grants, and financial insights." },
    { title: "Learning Hub", emoji: "🧠", desc: "Courses, guides, certifications, and 5 Whys problem solving." },
    { title: "Global Network", emoji: "🔗", desc: "Business profiles, matchmaking, mentors, and opportunity boards." },
    { title: "Global Dashboard", emoji: "📊", desc: "Analytics, risk alerts, and AI-driven insights." },
  ];

  const features = [
    { title: "Global Expansion Made Easy", emoji: "🌍", desc: "Move from local to global with structured guidance." },
    { title: "End-to-End Trade Platform", emoji: "📦", desc: "Handle everything from orders to delivery seamlessly." },
    { title: "AI-Powered Decisions", emoji: "🧠", desc: "Smart insights for expansion, pricing, and risk." },
    { title: "Integrated Ecosystem", emoji: "🔗", desc: "All modules connected into one super platform." },
    { title: "Secure & Compliant", emoji: "🔐", desc: "Built-in compliance and trust layers." },
    { title: "Scalable for All", emoji: "📈", desc: "Works for MSMEs, enterprises, and governments." },
  ];

  const futureFeatures = [
    { title: "AI Agents", emoji: "🤖", desc: "Autonomous assistants for decision-making and execution." },
    { title: "Digital Twin", emoji: "🧬", desc: "Simulate business, supply chains, and expansion strategies." },
    { title: "Predictive Intelligence", emoji: "🔮", desc: "Forecast demand, risks, and opportunities." },
    { title: "IoT Integration", emoji: "📡", desc: "Real-time tracking of logistics and operations." },
    { title: "Smart Contracts", emoji: "⛓️", desc: "Automated agreements and payments." },
    { title: "Metaverse Collaboration", emoji: "🌐", desc: "Virtual trade fairs, meetings, and training." },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-black text-white [&_p]:text-gray-50 [&_li]:text-gray-100">
        
      <nav className="sticky top-[20px] z-40 p-2 bg-black">
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab)}
              className={`text-xs lg:text-sm px-2 py-2 rounded-md ${
                activeTab === tab.id
                  ? "bg-green-700 text-yellow-200"
                  : "bg-green-900 hover:bg-green-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>
      <div className="flex justify-center items-center w-80 h-full bg-gray-700 mx-auto">
        <img
            src={image1}
            alt="TransNations About"
            className="w-full h-full object-contain"
        />
      </div>

      {/* Content */}
      <main className="flex-1 p-3">
        {activeTab === "ib" && (
            <section className="space-y-5 text-left max-w-4xl mx-auto">

                <h2 className="text-lg lg:text-xl font-bold text-yellow-200 text-center">💡 International Business</h2>
                <p className="text-xs lg:text-sm">
                International business refers to commercial activities that take place
                across national borders—including trade (exports/imports), investment,
                services, and global operations of companies.
                </p>
                <div>
                  <h3 className="text-lg font-semibold text-teal-300">
                      🌍 What is International Business?
                  </h3>
                  <p>
                      It involves buying, selling, producing, or investing in goods and
                      services between different countries. Companies expand globally to
                      access new markets, resources, talent, and cost advantages.
                  </p>
                </div>

                {/* Key Components */}
                <div>
                <h3 className="text-lg font-semibold text-teal-300">
                    🌐 Key Components
                </h3>
                <ul className="list-disc pl-5 space-y-2">
                    <li>
                    <strong>International Trade:</strong> Exports and imports of goods and services  
                    <br />Example: India exporting IT services to the US
                    </li>
                    <li>
                    <strong>Foreign Direct Investment (FDI):</strong> Investing in operations abroad  
                    <br />Example: Setting up a factory in another country
                    </li>
                    <li>
                    <strong>Multinational Corporations (MNCs):</strong> Companies operating globally  
                    <br />Examples: Apple, Toyota, Unilever
                    </li>
                    <li>
                    <strong>Global Supply Chains:</strong> Production across countries  
                    <br />Example: Design in US, manufacturing in China, selling worldwide
                    </li>
                </ul>
                </div>

                {/* Why Companies Go International */}
                <div>
                <h3 className="text-lg font-semibold text-teal-300">
                    🎯 Why Companies Go International
                </h3>
                <ul className="list-disc pl-5 space-y-1">
                    <li>Expand market size</li>
                    <li>Reduce costs (labor, raw materials)</li>
                    <li>Access new resources & technology</li>
                    <li>Diversify risk</li>
                    <li>Gain competitive advantage</li>
                </ul>
                </div>

                {/* Challenges */}
                <div>
                <h3 className="text-lg font-semibold text-teal-300">
                    ⚠️ Challenges
                </h3>
                <ul className="list-disc pl-5 space-y-1">
                    <li>Cultural differences 🌏</li>
                    <li>Legal and regulatory issues ⚖️</li>
                    <li>Currency fluctuations 💱</li>
                    <li>Political risks 🏛️</li>
                    <li>Supply chain disruptions 🚢</li>
                </ul>
                </div>

                {/* Strategies */}
                <div>
                <h3 className="text-lg font-semibold text-teal-300">
                    📊 Business Strategies
                </h3>
                <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Global Strategy:</strong> Standardized products worldwide</li>
                    <li><strong>Multi-domestic Strategy:</strong> Customized per country</li>
                    <li><strong>Transnational Strategy:</strong> Global efficiency + local adaptation</li>
                </ul>
                </div>

                {/* Example */}
                <div>
                <h3 className="text-lg font-semibold text-teal-300">
                    💡 Simple Example
                </h3>
                <p>
                    A smartphone brand designs products in one country, manufactures parts
                    in multiple countries, assembles in another, and sells globally.
                </p>
                <p className="text-yellow-300 font-medium">
                    👉 That entire process is international business in action.
                </p>
                </div>

            </section>
        )}
        {activeTab === "overview" && (
          <section className="space-y-4 text-center">
            <h2 className="text-lg lg:text-xl font-bold text-yellow-200">
              Global Business Operating System
            </h2>
            <p className="text-xs lg:text-sm">
              TransNation enables businesses, individuals, and institutions to
              expand, trade, work, and grow globally — seamlessly.
            </p>
            <p className="text-xs lg:text-sm">
              It combines expansion tools, trade systems, workforce platforms,
              funding access, and AI-powered intelligence into one unified
              platform.
            </p>
          </section>
        )}
        {activeTab === "modules" && (
          <section>
            <h2 className="text-xl font-bold text-yellow-200 text-center mb-4">
              Core Modules
            </h2>
            <div className="grid lg:grid-cols-3 gap-4">
              {modules.map((m, i) => (
                <div key={i} className="bg-gray-900 p-4 rounded-xl text-center">
                  <div className="text-2xl">{m.emoji}</div>
                  <h3 className="font-semibold mt-2">{m.title}</h3>
                  <p className="text-sm mt-1 text-gray-300">{m.desc}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Features */}
        {activeTab === "features" && (
          <section>
            <h2 className="text-xl font-bold text-yellow-200 text-center mb-4">
              Key Features
            </h2>
            <div className="grid lg:grid-cols-3 gap-4">
              {features.map((f, i) => (
                <div key={i} className="bg-gray-900 p-4 rounded-xl text-center">
                  <div className="text-2xl">{f.emoji}</div>
                  <h3 className="font-semibold mt-2">{f.title}</h3>
                  <p className="text-sm mt-1 text-gray-300">{f.desc}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Future */}
        {activeTab === "future" && (
          <section>
            <h2 className="text-xl font-bold text-yellow-200 text-center mb-4">
              TransNation X 🚀
            </h2>
            <div className="grid lg:grid-cols-3 gap-4">
              {futureFeatures.map((f, i) => (
                <div key={i} className="bg-gray-900 p-4 rounded-xl text-center">
                  <div className="text-2xl">{f.emoji}</div>
                  <h3 className="font-semibold mt-2">{f.title}</h3>
                  <p className="text-sm mt-1 text-gray-300">{f.desc}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* How to Start */}
        {activeTab === "start" && (
          <section className="text-center space-y-3">
            <h2 className="text-xl font-bold text-yellow-200">
              How to Start
            </h2>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>🌍 Explore global markets using Expansion Engine</li>
              <li>📦 Set up products and start trade</li>
              <li>🧾 Ensure compliance and documentation</li>
              <li>👨‍💻 Build global workforce</li>
              <li>💰 Access funding and scale</li>
              <li>🧠 Use AI insights to optimize growth</li>
            </ul>
          </section>
        )}
      </main>

      {/* Footer
      <footer className="border-t border-blue-400 py-3 text-center text-xs">
        <p>&copy; 2025 Think5Whys Global</p>
        <div className="flex justify-center gap-4 mt-2">
          <Link to="/about-us">About</Link>
          <Link to="/privacy-policy">Privacy</Link>
          <Link to="/terms-of-use">Terms</Link>
        </div>
      </footer> */}
    </div>
  );
};

export default TransNationAbout;